import React, { useEffect, useRef, useState } from 'react';
import './modal.css';

/**
 * Modal — used by ~39 screens in the old app, each with its own overlay
 * CSS. One implementation instead: Escape to close, click-outside to
 * close, body scroll locked while open, and a fixed footer for actions.
 *
 * `confirmClose`: pass `true` while the form inside has unsaved
 * changes. Escape, the overlay click, and the × button then ask
 * "discard changes?" instead of closing straight away and silently
 * losing what was typed — every form modal in the app shares this one
 * guard instead of each page re-implementing its own dirty/confirm
 * state (which only one screen, the exam form, used to have).
 *
 * `footer` may be a function `(requestClose) => node` instead of a
 * plain node — use this so the footer's own Cancel button goes through
 * the SAME guard instead of calling the raw onClose directly (a
 * misclick on Cancel loses just as much typed work as an accidental
 * outside click, so it deserves the same confirmation).
 */
export default function Modal({
  open = true,
  title,
  onClose,
  children,
  footer,
  size = 'md', // 'sm' | 'md' | 'lg'
  closeOnOverlay = true,
  confirmClose = false,
}) {
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const overlayRef = useRef(null);

  // A caller that flips confirmClose back to false mid-dialog (saved
  // successfully, for instance) shouldn't leave a stale confirm prompt
  // armed for next time it opens.
  useEffect(() => { if (!confirmClose) setConfirmingDiscard(false); }, [confirmClose]);

  const requestClose = () => {
    if (!onClose) return;
    if (confirmClose) setConfirmingDiscard(true);
    else onClose();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Several flows intentionally stack a small helper modal above a
      // larger form (for example, quick-create family above new student).
      // Only the topmost modal may consume Escape; otherwise one keypress
      // can arm discard prompts in both dialogs at once.
      const overlays = document.querySelectorAll('.ui-modal-overlay');
      if (overlays[overlays.length - 1] !== overlayRef.current) return;
      // While the discard-confirm itself is showing, Escape cancels
      // THAT prompt (stays open) rather than compounding into another.
      if (confirmingDiscard) { setConfirmingDiscard(false); return; }
      requestClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose, confirmClose, confirmingDiscard]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="ui-modal-overlay"
      onMouseDown={(e) => {
        if (closeOnOverlay && e.target === e.currentTarget && onClose) requestClose();
      }}
    >
      <div className={`ui-modal ui-modal-${size}`} role="dialog" aria-modal="true">
        <div className="ui-modal-head">
          <h2 className="ui-modal-title">{title}</h2>
          {onClose && (
            <button type="button" className="ui-modal-close" onClick={requestClose} title="إغلاق">✕</button>
          )}
        </div>
        <div className="ui-modal-body">{children}</div>
        {footer && (
          <div className="ui-modal-foot">
            {typeof footer === 'function' ? footer(requestClose) : footer}
          </div>
        )}

        {confirmingDiscard && (
          <div className="ui-modal-discard-overlay" onMouseDown={(e) => e.stopPropagation()}>
            <div className="ui-modal-discard-box">
              <p className="ui-modal-message">هناك تغييرات غير محفوظة. هل تريد الخروج وفقدانها؟</p>
              <div className="ui-modal-discard-actions">
                <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setConfirmingDiscard(false)}>
                  متابعة التعديل
                </button>
                <button type="button" className="ui-btn ui-btn-danger" onClick={onClose}>
                  تجاهل والخروج
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Confirmation dialog — replaces scattered window.confirm() calls and
 *  gives destructive actions a consistent, explicit look. */
export function ConfirmModal({ open, title, message, confirmText = 'تأكيد', cancelText = 'إلغاء', tone = 'danger', onConfirm, onCancel, busy = false }) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={busy ? undefined : onCancel}
      size="sm"
      footer={
        <>
          <button type="button" className="ui-btn ui-btn-secondary" onClick={onCancel} disabled={busy}>
            {cancelText}
          </button>
          <button type="button" className={`ui-btn ui-btn-${tone}`} onClick={onConfirm} disabled={busy} data-guide="confirm-ok">
            {busy ? '...' : confirmText}
          </button>
        </>
      }
    >
      <p className="ui-modal-message">{message}</p>
    </Modal>
  );
}
